import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useLang } from '../../context/LangContext';
import { useSession } from '../../context/SessionContext';
import { useSearch } from '../../context/SearchContext';
import { useCatalog } from '../../context/CatalogContext';

export default function DesktopNavbar() {
  const { lang, t, toggle } = useLang();
  const { itemCount } = useCart();
  const { sessionUser } = useSession();
  const { search, setSearch } = useSearch();
  const { activeSection, storeTabs, deliverTo, refreshDeliverTo } = useCatalog();
  const navigate = useNavigate();
  const location = useLocation();
  const te = lang === 'te';

  const activeStore = storeTabs.find((s) => s.id === activeSection) || storeTabs[0];
  const storeLabel = te ? (activeStore?.name_te || activeStore?.name_en || 'Groceries') : (activeStore?.name_en || 'Groceries');
  const deliverLabel = deliverTo?.label
    || (sessionUser?.name ? (te ? `${sessionUser.name} వద్దకు` : `Deliver to ${sessionUser.name}`) : null)
    || (te ? 'డెలివరీ స్థానం సెట్ చేయండి' : 'Set delivery location');

  const onSearchChange = (v) => {
    setSearch(v);
    if (v && location.pathname !== '/') navigate('/');
  };

  return (
    <header className="hidden md:block sticky top-0 z-40 bg-gradient-to-r from-brand-dark via-brand to-brand-glow shadow-lift">
      <div className="max-w-6xl mx-auto px-6 h-[76px] flex items-center gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0 group">
          <img src="/logo.jpeg" alt="NRK Mart" className="h-11 w-11 rounded-2xl object-cover shadow-soft ring-2 ring-white/40" />
          <div>
            <div className="font-black text-lg text-white leading-tight">{t.appName}</div>
            <div className="text-[11px] text-white/75">{te ? 'తాజా కూరగాయలు మీ ద్వారం వరకు' : 'Fresh from farm to your door'}</div>
          </div>
        </Link>

        <button
          type="button"
          onClick={refreshDeliverTo}
          className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 shrink-0 max-w-[220px]"
          title={te ? 'స్థానం నవీకరించు' : 'Update location'}
        >
          <span>📍</span>
          <span className="text-left min-w-0">
            <span className="block text-[10px] font-bold text-white/70 uppercase">{te ? 'డెలివరీ' : 'Deliver to'}</span>
            <span className="block text-xs font-extrabold text-white truncate">{deliverLabel}</span>
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-card focus-within:ring-2 focus-within:ring-gold/50">
            <span className="text-ink-faint">🔍</span>
            <input
              value={search || ''}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={te ? `${storeLabel}లో వెతకండి` : `Search ${storeLabel}`}
              className="flex-1 min-w-0 outline-none text-sm bg-transparent placeholder:text-ink-faint"
            />
            {search ? (
              <button type="button" onClick={() => onSearchChange?.('')} className="text-ink-faint hover:text-ink">✕</button>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={toggle}
            className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 font-extrabold text-xs text-white"
            title={te ? 'Switch to English' : 'తెలుగులోకి మార్చండి'}
          >
            {te ? 'EN' : 'తె'}
          </button>

          <button
            type="button"
            onClick={() => navigate(sessionUser ? '/profile' : '/signup')}
            className="px-3.5 py-2 rounded-xl hover:bg-white/15 text-left"
          >
            <span className="block text-[10px] font-bold text-white/65 uppercase">{sessionUser ? (te ? 'హలో' : 'Hello') : (te ? 'ఖాతా' : 'Account')}</span>
            <span className="block text-[13px] font-extrabold text-white truncate max-w-[110px]">
              {sessionUser ? (sessionUser.name || (te ? 'మీరు' : 'You')) : (te ? 'లాగిన్' : 'Sign in')}
            </span>
          </button>

          <button type="button" onClick={() => navigate('/orders')} className="px-3.5 py-2 rounded-xl hover:bg-white/15 text-left">
            <span className="block text-[10px] font-bold text-white/65 uppercase">{te ? 'రిటర్న్స్' : 'Returns'}</span>
            <span className="block text-[13px] font-extrabold text-white">{te ? '& ఆర్డర్లు' : '& Orders'}</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/cart')}
            className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gold text-white font-extrabold text-sm shadow-card hover:bg-gold-deep"
          >
            🛒 {te ? 'కార్ట్' : 'Cart'}
            {itemCount > 0 ? (
              <span className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-white text-brand-dark text-[10px] font-black flex items-center justify-center">
                {itemCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </header>
  );
}
